// Fiscal-period labeling for models whose fiscal year does NOT start in January.
// Presentation-edge only: transforms a period dimension VALUE (year / quarter /
// year-month) into a fiscal label like "FY2024 Q1". Calendar models (fiscal
// start = January) are never relabeled. The fiscal year is named by the
// calendar year in which the fiscal year STARTS — consistent with the backend
// `time_intel.as_of_range` window logic.

function _fiscalYear(year, month, fsm) {
  return month >= fsm ? year : year - 1;
}

function _fiscalQuarter(month, fsm) {
  const monthsIn = ((month - fsm) % 12 + 12) % 12; // 0..11
  return Math.floor(monthsIn / 3) + 1;
}

// Parse a dimension value into {kind, year, month?}. Returns null when the value
// is not a recognizable period (so ordinary text/number dims are left alone).
function _parsePeriod(value) {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  // Quarter forms: "2024-Q1", "2024Q1", "Q1 2024", "Q1-2024".
  let m = s.match(/^(\d{4})[-\s]?Q([1-4])$/i) || s.match(/^Q([1-4])[-\s]?(\d{4})$/i);
  if (m) {
    const isYearFirst = /^\d{4}/.test(s);
    const year = parseInt(isYearFirst ? m[1] : m[2], 10);
    const q = parseInt(isYearFirst ? m[2] : m[1], 10);
    return { kind: 'quarter', year, month: (q - 1) * 3 + 1 };
  }
  // Year-month forms: "2024-03", "2024/03", "202403". (NOT full dates.)
  m = s.match(/^(\d{4})[-/](\d{1,2})$/);
  if (m) {
    const month = parseInt(m[2], 10);
    if (month >= 1 && month <= 12) return { kind: 'month', year: parseInt(m[1], 10), month };
  }
  m = s.match(/^(\d{4})(\d{2})$/);
  if (m) {
    const month = parseInt(m[2], 10);
    if (month >= 1 && month <= 12) return { kind: 'month', year: parseInt(m[1], 10), month };
  }
  // Bare 4-digit year.
  if (/^\d{4}$/.test(s)) {
    const y = parseInt(s, 10);
    if (y >= 1900 && y <= 2200) return { kind: 'year', year: y };
  }
  return null;
}

const _MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Resolve the fiscal label of the CURRENT relative period (used by filter
// presets). Returns e.g. "FY2024 Q1" / "FY2024" / "FY2024 May", or null for
// calendar models / presets that have no fiscal framing.
export function currentFiscalPeriod(preset, fiscalStartMonth, asOfIso) {
  const fsm = Number(fiscalStartMonth) || 1;
  if (fsm === 1) return null;
  const d = asOfIso ? new Date(asOfIso + 'T00:00:00') : new Date();
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear(), m = d.getMonth() + 1;
  const fy = _fiscalYear(y, m, fsm);
  if (preset === 'this_year_to_date') return `FY${fy}`;
  if (preset === 'this_quarter_to_date') return `FY${fy} Q${_fiscalQuarter(m, fsm)}`;
  if (preset === 'this_month_to_date') return `FY${fy} ${_MONTHS[m - 1]}`;
  return null;
}

// Returns a labeler fn(value)->string|null, or null when the model is calendar.
export function makeFiscalPeriodLabeler(fiscalStartMonth) {
  const fsm = Number(fiscalStartMonth) || 1;
  if (fsm === 1) return null;
  return (value) => {
    const p = _parsePeriod(value);
    if (!p) return null;
    if (p.kind === 'year') return `FY${p.year}`;
    const fy = _fiscalYear(p.year, p.month, fsm);
    if (p.kind === 'quarter') return `FY${fy} Q${_fiscalQuarter(p.month, fsm)}`;
    return `FY${fy} ${_MONTHS[p.month - 1]}`;
  };
}
